import { useLazyQuery, useQuery } from '@apollo/client';
import { GET_ME, GET_TOP_CONTRIBUTIONS, Me, User } from '@frontend/data-access';
import { Card, Empty, List, Radio, Skeleton, Spin, Switch } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import UserAvatar from '../user-avatar/user-avatar';
import TopCard from './top-card';

const format = (value: string | number) => {
  if (!value) return 0;

  const num = Number(value);
  if (num > 1000000) {
    return '1,000,000+';
  }

  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function getOrdinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

export interface TopContributorsProps {
  limit?: number;
}

type TopFilter = {
  dateRange: string;
  limit: number;
  onlyA3: boolean;
};

type TopContributor = {
  user: User;
  point: number;
};

const CardSkeleton = () => (
  <Card className="!rounded-2xl">
    <Skeleton active avatar paragraph={{ rows: 4 }} />
  </Card>
);

export function TopContributors({
  limit: contributorsLimit,
}: TopContributorsProps) {
  const { t } = useTranslation('common');

  const [topParams, setTopParams] = useState<TopFilter>({
    dateRange: 'WEEKLY',
    limit: contributorsLimit ?? 20,
    onlyA3: false,
  });

  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const [getTopUsers, { data, loading }] = useLazyQuery<{
    getTopContributions: TopContributor[];
  }>(GET_TOP_CONTRIBUTIONS);

  const topUsers = data?.getTopContributions.slice(0, 3) || [];
  const users = data?.getTopContributions.slice(3) || [];

  useEffect(() => {
    getTopUsers({
      variables: {
        filter: topParams,
      },
    });
  }, [getTopUsers, topParams]);

  if (loading)
    return (
      <div className="text-center">
        <Spin />
      </div>
    );

  const maxPointLength = Math.max(
    ...users.map((u) => String(format(u.point)).length)
  );
  const columnWidth = `${Math.max(40, 40 + (maxPointLength - 3) * 12)}px`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap">
        <Radio.Group
          defaultValue="WEEKLY"
          value={topParams.dateRange}
          onChange={(e) =>
            setTopParams({ ...topParams, dateRange: e.target.value as string })
          }
        >
          <Radio.Button value="ALL">{t('all')}</Radio.Button>
          <Radio.Button value="MONTHLY">{t('last_month')}</Radio.Button>
          <Radio.Button value="WEEKLY">{t('last_week')}</Radio.Button>
        </Radio.Group>
        <div className="flex items-center space-x-2">
          <label>{t('only_a3_points')}</label>
          <Switch
            checked={topParams.onlyA3}
            onChange={(checked) =>
              setTopParams({ ...topParams, onlyA3: checked })
            }
          />
        </div>
      </div>
      <div className="space-y-6">
        {data?.getTopContributions?.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('no_contributors')}
          />
        )}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {loading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            topUsers.map((item, index) => (
              <TopCard
                key={item?.user.id}
                index={index + 1}
                user={item?.user}
                me={meData?.me.id}
                point={format(item?.point)}
              />
            ))
          )}
        </div>
        {(loading || (users && users.length > 0)) && (
          <div className="bg-white rounded-2xl border border-gray-400 overflow-x-scroll">
            <List
              dataSource={users}
              loading={loading}
              className="w-full"
              renderItem={(item, index) => (
                <List.Item
                  className={`!flex !p-4 gap-2 justify-between items-center transition-colors ${
                    meData?.me.id === item.user.id ? '!bg-gray-400' : ''
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="rounded-xl bg-gray-300 flex justify-center items-center w-10 h-10">
                      {index + 4}
                      <span className="text-[10px]">
                        {getOrdinal(index + 4)}
                      </span>
                    </div>
                    <UserAvatar user={item.user} size={40} showBadge />
                    <div>
                      <div className="text-gray-1000 font-medium">
                        {item.user.fullName}
                        {meData?.me.id === item.user.id && (
                          <span className="ml-1">(You)</span>
                        )}
                      </div>
                      <div>{item.user.profile?.title}</div>
                    </div>
                  </div>
                  <div
                    className="flex items-center justify-start"
                    style={{ width: columnWidth }}
                  >
                    <div className="space-y-0 text-left">
                      <div className="font-light text-sm">Points</div>
                      <div className="text-2xl leading-none">
                        {format(item?.point)}
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default TopContributors;
